import {z} from 'zod';
import {
	editEffectsSortOrder,
	type EditEffectsSortOrderParams,
	getSortField,
	type GetSortFieldParams,
	geSortOrder,
	type GetSortOrderParams,
	SortOrder,
	SortSchema,
} from '@netx/core-next/schema';
import {BrandPortalField, type IBrandPortal} from './brandPortalSchema';

export const SYSTEM_BRAND_PORTALS_SORTABLE_FIELDS = [
	BrandPortalField.label,
	BrandPortalField.path,
] as const;

export const SystemBrandPortalsSortSchema = SortSchema.omit({
	field: true,
}).extend({
	field: z.enum(SYSTEM_BRAND_PORTALS_SORTABLE_FIELDS),
});

export type SystemBrandPortalsSort = z.infer<
	typeof SystemBrandPortalsSortSchema
>;

export const SystemBrandPortalsSortableField = z.enum(
	SYSTEM_BRAND_PORTALS_SORTABLE_FIELDS,
).enum;

export const SYSTEM_BRAND_PORTALS_DEFAULT_SORT_FIELD =
	SystemBrandPortalsSortableField.label;

export const SYSTEM_BRAND_PORTALS_DEFAULT_SORT_ORDER = SortOrder.ASCENDING;

/** will return the sort passed in if valid/specified, otherwise will return the defaults for the invalid/unspecified */
export const getSystemBrandPortalsDefaultSort = (
	sort: Partial<SystemBrandPortalsSort> = {},
): SystemBrandPortalsSort => ({
	field: getSystemBrandPortalsSortField({sortField: sort.field}),
	order: getSystemBrandPortalsSortOrder({sortOrder: sort.order}),
});

/** will return the passed in sort field if valid, otherwise will retutn the default sort field */
export const getSystemBrandPortalsSortField = (
	params: Omit<
		GetSortFieldParams<typeof SYSTEM_BRAND_PORTALS_SORTABLE_FIELDS[number]>,
		'defaultSortField' | 'sortableFields'
	>,
) =>
	getSortField({
		...params,
		sortableFields: SYSTEM_BRAND_PORTALS_SORTABLE_FIELDS,
		defaultSortField: SYSTEM_BRAND_PORTALS_DEFAULT_SORT_FIELD,
	});

/** will return the passed in sort order if valid, otherwise will return the default sort order */
export const getSystemBrandPortalsSortOrder = (
	params: Omit<GetSortOrderParams, 'defaultSortOrder'>,
) =>
	geSortOrder({
		...params,
		defaultSortOrder: SYSTEM_BRAND_PORTALS_DEFAULT_SORT_ORDER,
	});

/** given the original data, the new data, and the current sort, determine if the sort order was effected */
export const editSystemBrandPortalsEffectsOrder = (
	params: Omit<
		EditEffectsSortOrderParams<
			IBrandPortal,
			typeof SYSTEM_BRAND_PORTALS_SORTABLE_FIELDS[number]
		>,
		'sortableFields'
	>,
) =>
	editEffectsSortOrder({
		...params,
		sortableFields: SYSTEM_BRAND_PORTALS_SORTABLE_FIELDS,
	});
